import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { exportsAPI } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Download,
  FileText,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  CalendarIcon,
  Trash2,
  History,
  Database,
  ShieldCheck,
  Info,
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { cn } from '../../lib/utils';

// Status badge mapping
const STATUS_CONFIG = {
  pending: { label: 'Pending', variant: 'secondary', icon: Clock },
  generating: { label: 'Generating', variant: 'default', icon: RefreshCw },
  ready: { label: 'Ready', variant: 'default', icon: CheckCircle },
  expired: { label: 'Expired', variant: 'secondary', icon: AlertTriangle },
  failed: { label: 'Failed', variant: 'destructive', icon: XCircle },
};

// Export type icons
const TYPE_ICONS = {
  calls: FileText,
  summaries: FileText,
  transfers: FileText,
  analytics: Database,
  compliance: ShieldCheck,
};

function DateRangePicker({ dateFrom, dateTo, onDateFromChange, onDateToChange }) {
  return (
    <div className="flex gap-2 items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-[180px] justify-start text-left font-normal',
              !dateFrom && 'text-muted-foreground'
            )}
            data-testid="date-from-trigger"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateFrom ? format(dateFrom, 'PPP') : 'Start date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateFrom}
            onSelect={onDateFromChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <span className="text-muted-foreground">to</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-[180px] justify-start text-left font-normal',
              !dateTo && 'text-muted-foreground'
            )}
            data-testid="date-to-trigger"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateTo ? format(dateTo, 'PPP') : 'End date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateTo}
            onSelect={onDateToChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant={config.variant} 
      className="gap-1"
      data-testid={`status-badge-${status}`}
    >
      <Icon className={cn('h-3 w-3', status === 'generating' && 'animate-spin')} />
      {config.label}
    </Badge>
  );
}

function ExportTypeCard({ type, selected, onSelect }) {
  const Icon = TYPE_ICONS[type.type] || FileText;
  
  return (
    <Card 
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        selected && 'ring-2 ring-primary border-primary'
      )}
      onClick={() => onSelect(type.type)}
      data-testid={`export-type-${type.type}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5 text-teal-500" />
          {type.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{type.description}</p>
        <p className="text-xs text-muted-foreground mt-2">
          {type.columns.length} columns
        </p>
      </CardContent>
    </Card>
  );
}

function ExportHistoryRow({ job, onDownload, onDelete, onRefresh }) {
  const isReady = job.status === 'ready';
  const canDownload = isReady && job.download_url;
  const isPolling = job.status === 'pending' || job.status === 'generating';
  
  // Auto-refresh polling exports
  useEffect(() => {
    if (isPolling) {
      const interval = setInterval(() => {
        onRefresh?.();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPolling, onRefresh]);
  
  return (
    <TableRow data-testid={`export-row-${job.id}`}>
      <TableCell>
        <div className="flex items-center gap-2">
          {TYPE_ICONS[job.export_type] && (
            React.createElement(TYPE_ICONS[job.export_type], { className: 'h-4 w-4 text-muted-foreground' })
          )}
          <span className="capitalize">{job.export_type}</span>
          {job.is_demo && (
            <Badge variant="outline" className="text-xs">DEMO</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm">
          {format(new Date(job.date_from), 'MMM d, yyyy')} - {format(new Date(job.date_to), 'MMM d, yyyy')}
        </div>
      </TableCell>
      <TableCell>
        {job.record_count?.toLocaleString() || '-'}
      </TableCell>
      <TableCell>
        <StatusBadge status={job.status} />
      </TableCell>
      <TableCell>
        {job.file_size ? `${(job.file_size / 1024).toFixed(1)} KB` : '-'}
      </TableCell>
      <TableCell>
        {job.expires_at && job.status === 'ready' ? (
          <span className="text-sm text-muted-foreground">
            {format(new Date(job.expires_at), 'MMM d, HH:mm')}
          </span>
        ) : '-'}
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          {canDownload && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDownload(job)}
              data-testid={`download-${job.id}`}
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(job.id)}
            data-testid={`delete-${job.id}`}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function ExportsReporting() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exportTypes, setExportTypes] = useState([]);
  const [exportHistory, setExportHistory] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [dateFrom, setDateFrom] = useState(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState(new Date());
  const [requesting, setRequesting] = useState(false);
  const [showNewExportDialog, setShowNewExportDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('new');
  
  // Check if user is demo
  const isDemo = user?.email?.toLowerCase() === 'demo@lockene.us';
  
  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [typesRes, historyRes] = await Promise.all([
        exportsAPI.getTypes(),
        exportsAPI.getHistory(50),
      ]);
      
      setExportTypes(typesRes.data || []);
      setExportHistory(historyRes.data || []);
    } catch (error) {
      console.error('Failed to load exports data:', error);
      toast.error('Failed to load exports data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);
  
  // Request new export
  const handleRequestExport = async () => {
    if (!selectedType) {
      toast.error('Please select an export type');
      return;
    }
    
    if (!dateFrom || !dateTo) {
      toast.error('Please select a date range');
      return;
    }
    
    try {
      setRequesting(true);
      
      const response = await exportsAPI.request(
        selectedType,
        startOfDay(dateFrom).toISOString(),
        endOfDay(dateTo).toISOString()
      );
      
      const job = response.data;
      
      if (job.status === 'ready') {
        toast.success(`Export ready! ${job.record_count?.toLocaleString() || 0} records.`);
      } else if (job.status === 'pending' || job.status === 'generating') {
        toast.info('Large export queued. Check history for status.');
      } else if (job.status === 'failed') {
        toast.error(`Export failed: ${job.error_message || 'Unknown error'}`);
      }
      
      setShowNewExportDialog(false);
      setSelectedType(null);
      setActiveTab('history');
      await loadData();
      
    } catch (error) {
      console.error('Export request failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create export');
    } finally {
      setRequesting(false);
    }
  };
  
  // Download export
  const handleDownload = async (job) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/api/exports/download/${job.id}`, { responseType: 'blob' });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition or generate
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${job.export_type}_export.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed');
    }
  };
  
  // Delete export
  const handleDelete = async (exportId) => {
    try {
      await exportsAPI.delete(exportId);
      toast.success('Export deleted');
      await loadData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete export');
    }
  };
  
  // Loading state
  if (authLoading || loading) {
    return (
      <div className="space-y-6" data-testid="exports-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="exports-reporting-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="exports-title">
            Exports & Reporting
          </h1>
          <p className="text-muted-foreground">
            Export Voice AI data for compliance, audits, and BI tools
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            data-testid="refresh-exports-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showNewExportDialog} onOpenChange={setShowNewExportDialog}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                data-testid="new-export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                New Export
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Export</DialogTitle>
                <DialogDescription>
                  Select data type and date range to export
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Export Type Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Export Type</label>
                  <div className="grid gap-3 md:grid-cols-2">
                    {exportTypes.map((type) => (
                      <ExportTypeCard
                        key={type.type}
                        type={type}
                        selected={selectedType === type.type}
                        onSelect={setSelectedType}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Date Range */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Date Range</label>
                  <DateRangePicker
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateFromChange={setDateFrom}
                    onDateToChange={setDateTo}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum range: 90 days
                  </p>
                </div>
                
                {/* Info */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Export Info</AlertTitle>
                  <AlertDescription>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>• Small exports (≤10,000 records) are ready immediately</li>
                      <li>• Large exports are processed in the background</li>
                      <li>• Export files are available for 24 hours</li>
                      {isDemo && <li>• <strong>Demo exports are labeled with DEMO flag</strong></li>}
                    </ul>
                  </AlertDescription>
                </Alert>
              </div>
              
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowNewExportDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRequestExport}
                  disabled={!selectedType || requesting}
                  data-testid="confirm-export-btn"
                >
                  {requesting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Export'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Demo Alert */}
      {isDemo && (
        <Alert data-testid="demo-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Demo Mode</AlertTitle>
          <AlertDescription>
            You are using a demo account. Exports will be clearly labeled with "DEMO" flag.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="exports-tabs">
          <TabsTrigger value="new" data-testid="tab-new">
            <Download className="h-4 w-4 mr-2" />
            Quick Export
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-4 w-4 mr-2" />
            Export History
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="new" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Export</CardTitle>
              <CardDescription>
                Select an export type to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {exportTypes.map((type) => (
                  <Card 
                    key={type.type}
                    className="cursor-pointer transition-all hover:shadow-md hover:border-primary"
                    onClick={() => {
                      setSelectedType(type.type);
                      setShowNewExportDialog(true);
                    }}
                    data-testid={`quick-export-${type.type}`}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        {TYPE_ICONS[type.type] && (
                          React.createElement(TYPE_ICONS[type.type], { className: 'h-5 w-5 text-teal-500' })
                        )}
                        {type.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{type.description}</p>
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Columns:</p>
                        <div className="flex flex-wrap gap-1">
                          {type.columns.slice(0, 5).map((col) => (
                            <Badge key={col} variant="secondary" className="text-xs">
                              {col}
                            </Badge>
                          ))}
                          {type.columns.length > 5 && (
                            <Badge variant="outline" className="text-xs">
                              +{type.columns.length - 5} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Export History</CardTitle>
              <CardDescription>
                View and download your recent exports
              </CardDescription>
            </CardHeader>
            <CardContent>
              {exportHistory.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="no-exports">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No exports yet</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowNewExportDialog(true)}
                  >
                    Create your first export
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="exports-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Date Range</TableHead>
                        <TableHead>Records</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exportHistory.map((job) => (
                        <ExportHistoryRow
                          key={job.id}
                          job={job}
                          onDownload={handleDownload}
                          onDelete={handleDelete}
                          onRefresh={loadData}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Info Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Export Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">CSV Format</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• UTF-8 encoding (Excel compatible)</li>
                <li>• Deterministic column order</li>
                <li>• UTC timestamps</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Retention</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Files available for 24 hours</li>
                <li>• Auto-deleted after expiration</li>
                <li>• All actions are audited</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">BI Integration</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Paginated API available</li>
                <li>• Max 1,000 records per page</li>
                <li>• Contact admin for API access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
